import { getInitDataRaw } from './telegram';
import type {
  Account,
  AccountListResponse,
  AdminStats,
  Booking,
  CatalogMeta,
  CatalogSkin,
  Me,
} from '../types';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number
  ) {
    super(message);
  }
}

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    Authorization: `tma ${getInitDataRaw()}`,
    ...(options.headers as Record<string, string> | undefined),
  };
  if (options.body && !(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(`/api${path}`, { ...options, headers });

  if (res.status === 204) return undefined as T;

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const body = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    const message = typeof body === 'object' && body?.error ? JSON.stringify(body.error) : String(body);
    throw new ApiError(message, res.status);
  }
  return body as T;
}

// ---- catalog ----
export const getCatalogSkins = () => apiFetch<CatalogSkin[]>('/catalog/skins');
export const getCatalogMeta = () => apiFetch<CatalogMeta>('/catalog/meta');

// ---- accounts ----
export const getAccounts = (query: string) =>
  apiFetch<AccountListResponse>(`/accounts${query ? `?${query}` : ''}`);
export const getAccount = (id: string) => apiFetch<Account>(`/accounts/${id}`);
export const createAccount = (data: Record<string, unknown>) =>
  apiFetch<Account>('/accounts', { method: 'POST', body: JSON.stringify(data) });
export const updateAccount = (id: string, data: Record<string, unknown>) =>
  apiFetch<Account>(`/accounts/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteAccount = (id: string) => apiFetch<void>(`/accounts/${id}`, { method: 'DELETE' });
export const uploadScreenshots = (id: string, files: File[]) => {
  const form = new FormData();
  files.forEach((f) => form.append('file', f));
  return apiFetch<{ id: string; url: string }[]>(`/accounts/${id}/screenshots`, {
    method: 'POST',
    body: form,
  });
};

// ---- game id verification ----
export interface VerifyGameIdResult {
  valid: boolean;
  name?: string;
  region?: string | null;
}
export const verifyGameId = (gameId: string, serverId: string) =>
  apiFetch<VerifyGameIdResult>('/verify-game-id', { method: 'POST', body: JSON.stringify({ gameId, serverId }) });

// ---- bookings ----
export const createBooking = (accountId: string, note?: string) =>
  apiFetch<{ id: string }>('/bookings', { method: 'POST', body: JSON.stringify({ accountId, note }) });
export const getMyBookings = () => apiFetch<Booking[]>('/bookings/my');
export const cancelBooking = (id: string) => apiFetch<void>(`/bookings/${id}`, { method: 'DELETE' });

// ---- me ----
export const getMe = () => apiFetch<Me>('/me');
export const getMyAccounts = () => apiFetch<Account[]>('/me/accounts');

// ---- favorites ----
export const getFavorites = () => apiFetch<Account[]>('/favorites');
export const addFavorite = (accountId: string) =>
  apiFetch<{ ok: boolean }>(`/favorites/${accountId}`, { method: 'POST' });
export const removeFavorite = (accountId: string) =>
  apiFetch<void>(`/favorites/${accountId}`, { method: 'DELETE' });

// ---- admin ----
export interface AdminBooking {
  id: string;
  status: string;
  note: string | null;
  createdAt: string;
  buyer: { id: string; username: string | null; firstName: string };
  account: Account;
}
export const getAdminAccounts = (status?: string) =>
  apiFetch<Account[]>(`/admin/accounts${status ? `?status=${status}` : ''}`);
export const getAdminBookings = () => apiFetch<AdminBooking[]>('/admin/bookings');
export const approveAccount = (id: string) =>
  apiFetch<Account>(`/admin/accounts/${id}/approve`, { method: 'POST' });
export const rejectAccount = (id: string, reason: string) =>
  apiFetch<Account>(`/admin/accounts/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) });
export const markSold = (id: string) =>
  apiFetch<Account>(`/admin/accounts/${id}/sold`, { method: 'POST' });
export const confirmBookingAdmin = (id: string) =>
  apiFetch<Booking>(`/admin/bookings/${id}/confirm`, { method: 'POST' });
export const cancelBookingAdmin = (id: string) =>
  apiFetch<void>(`/admin/bookings/${id}/cancel`, { method: 'POST' });
export const getAdminStats = () => apiFetch<AdminStats>('/admin/stats');
